import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Globe, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { CONTACT } from '../data/site';
import { track } from '../lib/analytics';
import SEO from '../components/SEO';
import { getSeo, SEO_KEYWORDS } from '../data/seo';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { name: '', company: '', email: '', phone: '', country: '', application: '', message: '', privacy: false, website: '' };

const Contact = () => {
  const { t, lang } = useLang();
  const seo = getSeo('contact', lang);
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState('idle'); // idle | sending | success | error

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    if (!form.privacy) { setState('error'); return; }
    setState('sending');
    try {
      const res = await fetch(`${BACKEND}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error('failed');
      setState('success');
      setForm(EMPTY);
      track('contact_submit');
    } catch {
      setState('error');
    }
  };

  const info = [
    { icon: MapPin, title: t('contact.addressTitle'), value: `${CONTACT.street}, ${CONTACT.city}, ${CONTACT.country}` },
    { icon: Mail, title: t('contact.emailTitle'), value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Phone, title: t('contact.phoneTitle'), value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, '')}` },
    { icon: Globe, title: t('contact.webTitle'), value: 'www.newchem.info', href: CONTACT.website },
  ];

  const inputCls =
    'w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-[14px] text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25 transition';
  const labelCls = 'block text-[13px] font-semibold text-slate-700 mb-1.5';

  const L = {
    emailLabel: lang === 'de' ? 'Gesch\u00e4ftliche E-Mail' : 'Business email',
    phone: lang === 'de' ? 'Telefon (optional)' : 'Phone (optional)',
    country: lang === 'de' ? 'Land (optional)' : 'Country (optional)',
    application: lang === 'de' ? 'Anwendung / Interesse (optional)' : 'Application / interest (optional)',
    privacyPre: lang === 'de' ? 'Ich habe die ' : 'I have read the ',
    privacyLink: lang === 'de' ? 'Datenschutzerkl\u00e4rung' : 'privacy policy',
    privacyPost: lang === 'de'
      ? ' gelesen und bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage verwendet werden.'
      : ' and agree that my details will be used to process my enquiry.',
    sending: lang === 'de' ? 'Wird gesendet\u2026' : 'Sending\u2026',
    success: lang === 'de'
      ? 'Vielen Dank. Ihre Anfrage wurde \u00fcbermittelt. Unser Team meldet sich zeitnah bei Ihnen.'
      : 'Thank you. Your enquiry has been submitted. Our team will get back to you shortly.',
    error: lang === 'de'
      ? 'Ihre Anfrage konnte derzeit nicht \u00fcbermittelt werden. Bitte versuchen Sie es sp\u00e4ter erneut oder schreiben Sie uns direkt an office@newchem.org.'
      : 'Your enquiry could not be submitted at the moment. Please try again later or email us directly at office@newchem.org.',
  };

  return (
    <div>
      <SEO title={seo.title} description={seo.description} path="/contact" lang={lang} keywords={SEO_KEYWORDS[lang]} />
      <PageHeader label={t('contact.label')} title={t('contact.title')} lead={t('contact.lead')} imageKey="labTest2" />
      <section className="bg-white py-16 lg:py-24">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Info */}
          <div className="lg:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-blue-700">{t('contact.officeTitle')}</p>
            <div className="mt-6 space-y-4">
              {info.map((it) => {
                const Icon = it.icon;
                return (
                  <div key={it.title} className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-700/5 border border-blue-700/15 text-blue-700 shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{it.title}</p>
                      {it.href ? (
                        <a
                          href={it.href}
                          target={it.href.startsWith('http') ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          className="mt-1 block text-[15px] font-semibold text-slate-800 hover:text-blue-700 transition-colors break-words"
                        >
                          {it.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-[15px] font-semibold text-slate-800">{it.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 w-full" data-testid="contact-form-panel">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 lg:p-9 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900">{t('contact.formTitle')}</h2>

              {state === 'success' && (
                <div data-testid="contact-success" className="mt-5 flex items-start gap-3 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-[14px] font-medium text-green-800">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  {L.success}
                </div>
              )}
              {state === 'error' && (
                <div data-testid="contact-error" className="mt-5 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[14px] font-medium text-red-800">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  {L.error}
                </div>
              )}

              <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('contact.name')} *</label>
                  <input data-testid="contact-name" name="name" value={form.name} onChange={onChange} required minLength={2} maxLength={100} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t('contact.company')} *</label>
                  <input data-testid="contact-company" name="company" value={form.company} onChange={onChange} required minLength={2} maxLength={120} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{L.emailLabel} *</label>
                  <input data-testid="contact-email" name="email" type="email" value={form.email} onChange={onChange} required maxLength={254} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{L.phone}</label>
                  <input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} maxLength={40} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{L.country}</label>
                  <input data-testid="contact-country" name="country" value={form.country} onChange={onChange} maxLength={60} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{L.application}</label>
                  <input data-testid="contact-application" name="application" value={form.application} onChange={onChange} maxLength={120} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>{t('contact.message')} *</label>
                  <textarea data-testid="contact-message" name="message" value={form.message} onChange={onChange} required minLength={10} maxLength={3000} rows={5} className={inputCls} />
                </div>

                {/* Honeypot: hidden from real users, checked server-side */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                  <label>Website</label>
                  <input name="website" value={form.website} onChange={onChange} tabIndex={-1} autoComplete="off" />
                </div>

                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 text-[13px] leading-relaxed text-slate-600">
                    <input
                      data-testid="contact-privacy"
                      name="privacy"
                      type="checkbox"
                      checked={form.privacy}
                      onChange={onChange}
                      required
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-700 focus:ring-blue-500/25"
                    />
                    <span>
                      {L.privacyPre}
                      <Link to="/datenschutz" className="font-semibold text-blue-700 hover:underline">{L.privacyLink}</Link>
                      {L.privacyPost}
                    </span>
                  </label>
                </div>

                <div className="sm:col-span-2">
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={state === 'sending'}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-blue-700 text-white text-[14px] font-semibold hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {state === 'sending' ? L.sending : t('contact.send')}
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
